import { useMemo, useState } from 'react';
import { useData, useList, useSession } from '@yingbaba/api';
import { ActionDialog, Empty, Notice, PageTitle, Pagination, QueryState } from '@yingbaba/ui';
import { datetime, money } from '@yingbaba/utils';

type CommissionTier = {
  up_to_minor: number;
  rate_bps: number;
};

type CommissionRule = {
  id: string;
  created_at: string;
  profile: string;
  version: number;
  rate_bps: number;
  reason: string;
  tiers: CommissionTier[];
};

type Organization = {
  id: string;
  name: string;
  city: string;
  capabilities: string;
  commission_profile: string;
};

const profileDefinitions = [
  {
    value: 'film_company',
    label: '影视公司',
    description: '通过平台承接影视项目，按订单总金额选择阶梯费率。',
    defaults: [
      { up_to_minor: 1_000_000, rate_bps: 200 },
      { up_to_minor: 3_000_000, rate_bps: 300 },
      { up_to_minor: 5_000_000, rate_bps: 500 },
      { up_to_minor: 10_000_000, rate_bps: 800 },
      { up_to_minor: 0, rate_bps: 1000 },
    ],
  },
  {
    value: 'material_creator',
    label: '素材创作者',
    description: '创作者在平台销售影视素材，不区分成交金额。',
    defaults: [{ up_to_minor: 0, rate_bps: 200 }],
  },
  {
    value: 'supply_chain',
    label: '供应链公司',
    description: '器材租赁、配音公司、模特经纪、灯光公司等资源供应商。',
    defaults: [{ up_to_minor: 0, rate_bps: 1000 }],
  },
  {
    value: 'individual_creator',
    label: '个人创作者',
    description: '导演、摄影师、美术指导、编导等个人在平台承接业务。',
    defaults: [{ up_to_minor: 0, rate_bps: 200 }],
  },
] as const;

const profileOptions = [
  { value: '', label: '未配置（禁止创建新订单）' },
  ...profileDefinitions.map((profile) => ({ value: profile.value, label: profile.label })),
];

function profileLabel(profile: string) {
  return (
    profileDefinitions.find((item) => item.value === profile)?.label ||
    (profile ? profile : '旧版全局规则')
  );
}

function percent(rateBPS: number) {
  return `${rateBPS / 100}%`;
}

function ruleText(tiers: CommissionTier[]) {
  if (!tiers?.length) return '—';
  if (tiers.length === 1 && tiers[0].up_to_minor === 0) return percent(tiers[0].rate_bps);
  return tiers
    .map((tier, index) => {
      const lower = index === 0 ? 0 : tiers[index - 1].up_to_minor;
      const range = tier.up_to_minor
        ? `${index === 0 ? '≤' : `${money(lower)} < 金额 ≤ `}${money(tier.up_to_minor)}`
        : `> ${money(lower)}`;
      return `${range}：${percent(tier.rate_bps)}`;
    })
    .join('；');
}

function rateValue(tier: CommissionTier) {
  return String(tier.rate_bps / 100);
}

function tierEditorFields(tiers: readonly CommissionTier[]) {
  return tiers.flatMap((tier, index) => {
    const fields = [];
    if (index < tiers.length - 1) {
      fields.push({
        name: `limit${index}`,
        label: `第 ${index + 1} 档上限（元）`,
        type: 'number',
        min: 0.01,
        step: 0.01,
        value: String(tier.up_to_minor / 100),
      });
    }
    fields.push({
      name: `rate${index}`,
      label: index === tiers.length - 1 ? '最后一档费率（%）' : `第 ${index + 1} 档费率（%）`,
      type: 'number',
      min: 0,
      max: 10,
      step: 0.01,
      value: rateValue(tier),
    });
    return fields;
  });
}

function tiersFromEditor(value: Record<string, string>, current: readonly CommissionTier[]) {
  return current.map((_, index) => ({
    up_to_minor:
      index === current.length - 1 ? 0 : Math.round(Number(value[`limit${index}`]) * 100),
    rate_bps: Math.round(Number(value[`rate${index}`]) * 100),
  }));
}

export function CommissionSettings() {
  const session = useSession();
  const canEdit = ['super_admin', 'finance'].includes(session.data?.account.platform_role || '');
  const [organizationPage, setOrganizationPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const currentRules = useData<CommissionRule[]>('/admin/commission/current');
  const ruleHistory = useList<CommissionRule>(`/admin/commission?page=${historyPage}&page_size=50`);
  const organizations = useList<Organization>(
    `/admin/commission/organizations?page=${organizationPage}&page_size=50`,
  );
  const latest = useMemo(() => {
    const result = new Map<string, CommissionRule>();
    for (const rule of currentRules.data || []) {
      if (rule.profile) result.set(rule.profile, rule);
    }
    return result;
  }, [currentRules.data]);
  const sellers = organizations.data?.items || [];

  return (
    <>
      <PageTitle
        title="佣金规则"
        description="费率按服务商身份独立版本化。新规则只影响之后创建的订单，历史订单保留原规则版本。"
      />
      <Notice>
        影视公司阶梯按<strong>订单总金额</strong>
        选择一个费率，不做分段累进计算；费率可动态调整但最高不超过
        10%。服务商和资源的费率身份必须由平台审核确认，不能由供应方提交的分类直接决定。
      </Notice>
      <QueryState query={currentRules}>
        <div className="work-grid" style={{ marginTop: 20 }}>
          {profileDefinitions.map((definition) => {
            const current = latest.get(definition.value);
            const tiers = current?.tiers?.length
              ? current.tiers
              : definition.defaults.map((tier) => ({ ...tier }));
            return (
              <section className="panel" key={definition.value}>
                <div className="panel-title">
                  <div>
                    <h3>{definition.label}</h3>
                    <p className="muted" style={{ marginTop: 6 }}>
                      {definition.description}
                    </p>
                  </div>
                  {canEdit &&
                    (definition.value === 'film_company' ? (
                      <ActionDialog
                        title="调整影视公司佣金"
                        intent="financial"
                        submitLabel="发布新规则版本"
                        trigger="调整规则"
                        path="/admin/commission"
                        description={`保持当前 ${tiers.length} 档结构进行修改；最后一档始终表示无上限。若当前规则是单档，保存后仍保持单档。`}
                        fields={[
                          ...tierEditorFields(tiers),
                          { name: 'reason', label: '变更原因', type: 'textarea', min: 2 },
                        ]}
                        transform={(value) => ({
                          profile: definition.value,
                          tiers: tiersFromEditor(value, tiers),
                          reason: value.reason,
                        })}
                      />
                    ) : (
                      <ActionDialog
                        title={`调整${definition.label}佣金`}
                        intent="financial"
                        submitLabel="发布新规则版本"
                        trigger="调整费率"
                        path="/admin/commission"
                        fields={[
                          {
                            name: 'rate',
                            label: '佣金费率（%）',
                            type: 'number',
                            min: 0,
                            max: 10,
                            step: 0.01,
                            value: rateValue(tiers[0]),
                          },
                          { name: 'reason', label: '变更原因', type: 'textarea', min: 2 },
                        ]}
                        transform={(value) => ({
                          profile: definition.value,
                          tiers: [
                            { up_to_minor: 0, rate_bps: Math.round(Number(value.rate) * 100) },
                          ],
                          reason: value.reason,
                        })}
                      />
                    ))}
                </div>
                <p className="prose" style={{ marginTop: 16 }}>
                  {ruleText(tiers)}
                </p>
                <p className="small muted" style={{ marginTop: 12 }}>
                  {current
                    ? `当前版本 V${current.version} · ${datetime(current.created_at)}`
                    : '尚未持久化规则，将使用系统默认值'}
                </p>
              </section>
            );
          })}
        </div>
      </QueryState>

      <section className="panel" style={{ marginTop: 24 }}>
        <div className="panel-title">
          <div>
            <h3>服务商费率身份</h3>
            <p className="muted" style={{ marginTop: 6 }}>
              财务可调整审核后的组织身份。未配置费率身份的服务商不能创建新订单，避免个人创作者被误按影视公司阶梯计费。
            </p>
          </div>
        </div>
        <QueryState query={organizations}>
          {sellers.length ? (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>组织</th>
                      <th>业务能力</th>
                      <th>费率身份</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellers.map((organization) => (
                      <tr key={organization.id}>
                        <td>
                          <strong>{organization.name}</strong>
                          <span className="sub-cell">{organization.city || '—'}</span>
                        </td>
                        <td>{organization.capabilities}</td>
                        <td>
                          {organization.commission_profile
                            ? profileLabel(organization.commission_profile)
                            : '未配置'}
                        </td>
                        <td>
                          {canEdit ? (
                            <ActionDialog
                              title="设置服务商费率身份"
                              intent="financial"
                              submitLabel="保存身份"
                              trigger="设置"
                              path={`/admin/organizations/${organization.id}/commission-profile`}
                              description="身份变化只影响之后创建的订单，不改写已有订单佣金。清空身份会阻止该服务商创建新订单。"
                              fields={[
                                {
                                  name: 'profile',
                                  label: '费率身份',
                                  options: profileOptions,
                                  value: organization.commission_profile || '',
                                },
                                { name: 'reason', label: '调整原因', type: 'textarea', min: 2 },
                              ]}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={organizationPage}
                total={organizations.data?.total || 0}
                size={50}
                onPage={setOrganizationPage}
              />
            </>
          ) : (
            <Empty title="暂无服务商组织" description="服务商或资源供应商入驻后会出现在这里。" />
          )}
        </QueryState>
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <div className="panel-title">
          <div>
            <h3>规则历史</h3>
            <p className="muted" style={{ marginTop: 6 }}>
              订单保存规则版本和实际费率，用于审计、退款与分账追溯。
            </p>
          </div>
        </div>
        <QueryState query={ruleHistory}>
          {ruleHistory.data?.items.length ? (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>版本</th>
                      <th>服务商身份</th>
                      <th>规则</th>
                      <th>原因</th>
                      <th>生效时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ruleHistory.data.items.map((rule) => (
                      <tr key={rule.id}>
                        <td>V{rule.version}</td>
                        <td>{profileLabel(rule.profile)}</td>
                        <td style={{ maxWidth: 520 }}>{ruleText(rule.tiers)}</td>
                        <td>{rule.reason}</td>
                        <td>{datetime(rule.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={historyPage}
                total={ruleHistory.data?.total || 0}
                size={50}
                onPage={setHistoryPage}
              />
            </>
          ) : (
            <Empty title="暂无佣金规则" />
          )}
        </QueryState>
      </section>
    </>
  );
}
